import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, FlatList, HStack, IconButton, Spacer, VStack } from 'native-base';
import { isEmpty, map, orderBy, startCase, sumBy, toLower, values } from 'lodash';

import { ArrowSquareUpRightIcon, ButtonGroup, CleanIcon, Modal, Text } from '@pimm/base';
import {
  AggAssignedTaskDto,
  AggCleaningTask,
  AggPositionAssigneeDto,
  AggScheduleTypeDto,
  CleaningScheduleTypeEnum,
  CleaningTaskStatusEnum,
} from '@pimm/services/lib/sms-workforce';
import { useSiteConfig, useSiteTime } from '@app/features/store-core';
import { stringToDateLocal } from '@app/utils/date-formatter';
import { useModalFocus } from '@app/hooks/modal-focus.hook';
import { CleaningDocuments } from './cleaning-documents';
import { CleaningTasksItem } from './cleaning-tasks-item';

enum CleaningTasksTabItem {
  ALL = 'All',
  OVERDUE = 'Overdue',
  DUE_TODAY = 'Due Today',
  COMPLETED = 'Completed',
}

export interface CleaningTaskAssignee {
  id?: number;
  employeeId?: string;
  firstName?: string;
  lastName?: string;
  dueDate?: Date;
  cleaningTaskStatus?: CleaningTaskStatusEnum;
  scheduleType?: CleaningScheduleTypeEnum;
  cleaningTaskAssignmentId?: number;
  cleaningTaskId?: number;
  statusDate?: Date;
}

interface CleaningTasksProps {
  assignee?: AggPositionAssigneeDto;
  empTasks?: AggCleaningTask;
  positionTasks?: AggCleaningTask;
  positionTitle?: string;
  slotHasEmployee?: boolean;
  isSmallScreen: boolean;
  onNavigateTo: (screen: string, params?: any) => void;
}

type ScheduleGroup = { emp: AggScheduleTypeDto[]; pos: AggScheduleTypeDto[] };

export const CleaningTasks = ({
  assignee,
  empTasks,
  positionTasks,
  positionTitle,
  slotHasEmployee,
  isSmallScreen,
  onNavigateTo,
}: CleaningTasksProps) => {
  const { t } = useTranslation();
  const siteTime = useSiteTime();
  const { siteConfig } = useSiteConfig();
  const modalCleaningTask = useModalFocus<AggAssignedTaskDto>();

  const [tabFocus, setTabFocus] = useState<string>(CleaningTasksTabItem.DUE_TODAY);
  const [scheduleTypes] = useState<CleaningScheduleTypeEnum[]>([
    CleaningScheduleTypeEnum.Daily,
    CleaningScheduleTypeEnum.Weekly,
    CleaningScheduleTypeEnum.Monthly,
  ]);

  const isEmptyCleaningTasks = isEmpty(empTasks) && isEmpty(positionTasks);

  const categories = useMemo(() => {
    const now = siteTime.today();
    const sortedEmp = map(empTasks?.scheduleTypes, schedule => ({
      ...schedule,
      assignedTasks: orderBy(schedule.assignedTasks, ['dueDate', 'title'], ['asc', 'asc']),
    }));
    const sortedPos = map(positionTasks?.scheduleTypes, schedule => ({
      ...schedule,
      assignedTasks: orderBy(schedule.assignedTasks, ['dueDate', 'title'], ['asc', 'asc']),
    }));

    const isSameCalendarDay = (a: Date, b: Date) =>
      a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

    const filterTasks = (schedules: AggScheduleTypeDto[], tabItem: string): AggScheduleTypeDto[] =>
      schedules.map(schedule => ({
        ...schedule,
        assignedTasks: schedule.assignedTasks?.filter(task => {
          if (task.cleaningTaskStatus === CleaningTaskStatusEnum.NotApplicable) return false;
          if (tabItem === CleaningTasksTabItem.ALL) return true;
          if (tabItem === CleaningTasksTabItem.COMPLETED) return task.cleaningTaskStatus === CleaningTaskStatusEnum.Complete;
          if (task.cleaningTaskStatus === CleaningTaskStatusEnum.Complete) return false;

          const dueDate = stringToDateLocal(task.dueDate);
          if (tabItem === CleaningTasksTabItem.OVERDUE) return dueDate && dueDate < now;
          // Due Today: same calendar day regardless of whether the task is already overdue.
          // An overdue-today task is still "due today" and should appear in the default tab.
          return dueDate && isSameCalendarDay(dueDate, now);
        }),
      }));

    return values(CleaningTasksTabItem).reduce((acc, tabItem) => {
      acc[tabItem] = { emp: filterTasks(sortedEmp, tabItem), pos: filterTasks(sortedPos, tabItem) };
      return acc;
    }, {} as Record<string, ScheduleGroup>);
  }, [empTasks, positionTasks]);

  const assigneeFirstName = startCase(toLower(assignee?.name?.split(' ')[0] ?? '')) || 'Employee';

  return (
    <>
      <FlatList
        h="full"
        flex={1}
        m={!isSmallScreen && isEmptyCleaningTasks ? 4 : 0}
        pb={3}
        data={!isEmptyCleaningTasks ? scheduleTypes : []}
        extraData={[assignee, categories, isEmptyCleaningTasks, tabFocus, siteConfig.id]}
        borderColor="gray.200"
        borderWidth={isEmptyCleaningTasks && !isSmallScreen ? 1 : 0}
        backgroundColor={isEmptyCleaningTasks ? 'gray.25' : undefined}
        borderRadius={8}
        stickyHeaderIndices={[0]}
        contentContainerStyle={{
          flexGrow: isEmptyCleaningTasks ? 1 : 0,
        }}
        scrollEnabled
        ListHeaderComponent={() => (
          <VStack space={2} p={isEmptyCleaningTasks ? 3 : 4} pb={0} bg={isEmptyCleaningTasks ? 'gray.25' : 'white'}>
            <HStack space={2} alignItems="center">
              <CleanIcon size="md" color="black" />
              <Text size="lg" fontWeight={700} color="black">
                {t('position_guide:cleaning_tasks_current_week')}
              </Text>
              <Spacer />
              <IconButton
                p={0}
                rounded="md"
                w={8}
                h={8}
                borderWidth={1}
                borderColor="gray.300"
                backgroundColor="white"
                _hover={{ bg: undefined }}
                _pressed={{ bg: undefined }}
                icon={<ArrowSquareUpRightIcon color="gray.700" />}
                onPress={() => onNavigateTo('CleaningTasks')}
              />
            </HStack>
            {!isEmptyCleaningTasks && (
              <ButtonGroup value={tabFocus} flex={1} onChange={setTabFocus}>
                {values(CleaningTasksTabItem).map(tabItem => {
                  const group = categories[tabItem] as ScheduleGroup;
                  const count =
                    sumBy(group?.emp ?? [], _ => _.assignedTasks?.length || 0) + sumBy(group?.pos ?? [], _ => _.assignedTasks?.length || 0);
                  return (
                    <ButtonGroup.Item key={tabItem} flex={1} value={tabItem} w={100} badgeIcon={{ show: true, value: count }} h={30}>
                      {t(`position_guide:${tabItem.toLowerCase().replace(/\s+/g, '_')}`)}
                    </ButtonGroup.Item>
                  );
                })}
              </ButtonGroup>
            )}
          </VStack>
        )}
        ListEmptyComponent={() => (
          <Box flex={1} alignItems="center" justifyContent="center">
            <Text size="md" fontWeight={500} color="gray.700">
              {t('position_guide:no_assigned_tasks')}
            </Text>
          </Box>
        )}
        renderItem={({ item: scheduleType }) => {
          const group = categories[tabFocus] as ScheduleGroup;
          const empAssigned = group?.emp?.find(_ => _.scheduleType === scheduleType)?.assignedTasks ?? [];
          const posAssigned = group?.pos?.find(_ => _.scheduleType === scheduleType)?.assignedTasks ?? [];
          const totalCount = empAssigned.length + posAssigned.length;

          if (totalCount === 0) return null;

          return (
            <VStack key={scheduleType} rounded="lg" mx={4} mt={3} p={2.5} pt={1} borderWidth={1} bg="gray.25">
              <HStack>
                <Text size="lg" lineHeight="2xl" fontWeight={700} color="black" textTransform="uppercase">
                  {t(`position_guide:${scheduleType.replace(/\s+/g, '').toLowerCase()}`)}
                </Text>
                <Spacer />
                <Text size="lg" lineHeight="2xl" fontWeight={700} color="black">
                  {totalCount}
                </Text>
              </HStack>

              {/* Employee tasks sub-section */}
              {empAssigned.length > 0 && (
                <VStack space={1} mt={1.5}>
                  <HStack space={1.5} alignItems="center">
                    <Box w={2} h={2} rounded="full" bg="primary.500" />
                    <Text size="sm" fontWeight={600} color="gray.600" flex={1}>
                      {`${t('position_guide:assigned_to')} ${assigneeFirstName}`}
                    </Text>
                    <Text size="sm" color="gray.500">
                      {empAssigned.length}
                    </Text>
                  </HStack>
                  {empAssigned.map(task => (
                    <CleaningTasksItem
                      key={`${tabFocus}.emp.${task.cleaningTaskId}.${task.cleaningTaskAssignmentId}`}
                      cleaningTask={task}
                      employeeId={assignee?.employeeId}
                      empName={assignee?.name}
                      scheduleType={scheduleType}
                      siteId={siteConfig.id}
                      slotHasEmployee={slotHasEmployee}
                      onPressLinkDocument={modalCleaningTask.setOpen}
                    />
                  ))}
                </VStack>
              )}

              {/* Position tasks sub-section */}
              {posAssigned.length > 0 && (
                <VStack space={1} mt={empAssigned.length > 0 ? 3 : 1.5}>
                  <HStack space={1.5} alignItems="center">
                    <Box w={2} h={2} rounded="full" bg="gray.400" />
                    <Text size="sm" fontWeight={600} color="gray.600" flex={1}>
                      {positionTitle ?? 'Position'} {t('position_guide:tasks').toLowerCase()}
                    </Text>
                    <Text size="sm" color="gray.500">
                      {posAssigned.length}
                    </Text>
                  </HStack>
                  {posAssigned.map(task => {
                    const posEmpName = (task as any).firstName
                      ? [(task as any).firstName, (task as any).lastName].filter(Boolean).join(' ')
                      : undefined;
                    return (
                      <CleaningTasksItem
                        key={`${tabFocus}.pos.${task.cleaningTaskId}.${task.cleaningTaskAssignmentId}`}
                        cleaningTask={task}
                        employeeId={assignee?.employeeId}
                        empName={posEmpName}
                        scheduleType={scheduleType}
                        siteId={siteConfig.id}
                        slotHasEmployee={slotHasEmployee}
                        onPressLinkDocument={modalCleaningTask.setOpen}
                      />
                    );
                  })}
                </VStack>
              )}
            </VStack>
          );
        }}
      />

      <Modal
        _content={{ rounded: '2xl', w: '90%', h: '95%', maxWidth: '1200', overflow: 'hidden' }}
        size="full"
        noPadding
        hideClose
        isOpen={modalCleaningTask.isOpen}
        onClose={modalCleaningTask.setHide}
      >
        {!!modalCleaningTask.payload && <CleaningDocuments cleaningTask={modalCleaningTask.payload} onClose={modalCleaningTask.setHide} />}
      </Modal>
    </>
  );
};
